import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "../utils/index";
import { Spinner } from "./spinner";

/*
 * Press, not upstream: a 3% squeeze that springs back (design prototype), instead of the 1px nudge.
 * Only the scale springs; colours keep upstream's 150ms ease, since an overshooting curve
 * would push a hover fill past its target colour before settling.
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap [transition:color_150ms_cubic-bezier(0.4,0,0.2,1),background-color_150ms_cubic-bezier(0.4,0,0.2,1),border-color_150ms_cubic-bezier(0.4,0,0.2,1),box-shadow_150ms_cubic-bezier(0.4,0,0.2,1),opacity_150ms_cubic-bezier(0.4,0,0.2,1),scale_180ms_cubic-bezier(0.34,1.56,0.64,1)] outline-none select-none cursor-pointer focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        unstyled: "",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // `default` keeps its pre-Figma size for casino's callers; cashier uses `2xl`.
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 [&_svg:not([class*='size-'])]:size-3",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2",
        xl: "h-10 md:h-11! gap-2 px-4 has-data-[icon=inline-end]:pe-3 has-data-[icon=inline-start]:ps-3",
        icon: "border-none size-8",
        "icon-xs":
          "border-none size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "border-none size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "border-none size-9",
        "icon-xl": "border-none size-10 md:size-11",
        // Figma sizes used by Cashier.
        sm: "h-8 gap-1.5 rounded-md px-2 text-xs font-semibold in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 [&_svg:not([class*='size-'])]:size-4",
        md: "h-10 gap-2 rounded-md px-2.5 font-semibold has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2 [&_svg:not([class*='size-'])]:size-5",
        "2xl":
          "h-12 gap-2 rounded-lg px-3 text-base font-semibold has-data-[icon=inline-end]:pe-2.5 has-data-[icon=inline-start]:ps-2.5 [&_svg:not([class*='size-'])]:size-5",
        "3xl":
          "h-14 gap-3 rounded-lg px-4 text-base font-semibold has-data-[icon=inline-end]:pe-3 has-data-[icon=inline-start]:ps-3 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

const SPINNER_SIZES: Record<ButtonSize, string> = {
  xs: "size-4",
  sm: "size-4",
  "icon-xs": "size-4",
  "icon-sm": "size-4",
  default: "size-6",
  md: "size-6",
  lg: "size-6",
  xl: "size-6",
  "2xl": "size-6",
  "3xl": "size-6",
  icon: "size-6",
  "icon-lg": "size-6",
  "icon-xl": "size-6",
};

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  disabled,
  type = "button",
  onClick,
  onClickCapture,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /**
     * Render the child as the button, rather than nesting one inside it.
     *
     * Combining this with `disabled` or `loading` makes the Button
     * **client-only**. `disabled` is inert on an anchor and `:disabled` never
     * matches one, so that state is enforced with handlers instead, and this
     * file carries no `use client`. A server component rendering
     * `<Button asChild disabled>` throws "Event handlers cannot be passed to
     * Client Component props" at request time, with production showing a
     * digest in place of the route. Put such a Button behind `use client`.
     *
     * Every other combination renders on the server: the component attaches no
     * handler unless a caller passed one.
     */
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";
  const isDisabled = loading || disabled;

  const showLoader = loading && !asChild;

  // `disabled` is inert on an anchor, so the state is expressed by class.
  const disabledAsChild = asChild && isDisabled;

  /*
   * Both stay undefined unless something needs them. This component carries no
   * `use client`, so a server component rendering a plain Button would
   * otherwise be handed two function props and React refuses to serialise
   * those: "Event handlers cannot be passed to Client Component props". Only a
   * caller that passed a handler, or the disabled guard below, can make one.
   */
  const needsGuard = disabledAsChild;

  /*
   * Capture, not bubble: Slot runs the child's handler first. Composed, not
   * overwritten: a consumer's own capture handler has to still run.
   */
  const blockActivation =
    needsGuard || onClickCapture
      ? (event: React.MouseEvent<HTMLButtonElement>) => {
          if (disabledAsChild) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClickCapture?.(event);
        }
      : undefined;

  const handleClick =
    needsGuard || onClick
      ? (event: React.MouseEvent<HTMLButtonElement>) => {
          if (disabledAsChild) return;
          onClick?.(event);
        }
      : undefined;

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      data-disabled={disabledAsChild || undefined}
      aria-busy={loading || undefined}
      aria-disabled={disabledAsChild || undefined}
      disabled={asChild ? undefined : isDisabled}
      // Deviation from shadcn: a bare <button> in a form submits it.
      type={asChild ? undefined : type}
      className={cn(buttonVariants({ variant, size, className }), {
        relative: showLoader,
        // Loading disables it; without this it would read as unavailable.
        "disabled:opacity-100": showLoader && !disabled,
        "pointer-events-none": disabledAsChild,
        "opacity-50": disabledAsChild && !loading,
      })}
      {...props}
      onClickCapture={blockActivation}
      onClick={handleClick}
    >
      {showLoader ? (
        <>
          <span className="opacity-0">{children}</span>
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center"
          >
            <Spinner
              role={undefined}
              aria-label={undefined}
              className={SPINNER_SIZES[size ?? "default"]}
            />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
